<script setup lang="ts">
/**
 * Spend-budget admin (ADR 0049).
 *
 * Two things on one page, because they answer one question:
 *   - The **policy** — what every principal gets by default (daily +
 *     monthly limit, currency, USD→currency rate, warn threshold, and
 *     whether hitting a limit actually blocks).
 *   - The **overrides** — per-principal exceptions, each shown with its
 *     live day/month spend so you can see who is near a wall before they
 *     hit it.
 *
 * Limits are typed in the display currency (DKK), not USD. The rate card
 * prices in USD and `usd_rate` converts, so moving the FX rate moves the
 * measured spend, never the stated limit.
 */
import { vWrite } from "../admin-read-only";
import { computed, onMounted, ref } from "vue";
import {
  AdminError,
  type BudgetOverride,
  type BudgetPolicy,
  deleteBudgetOverride,
  getBudgets,
  saveBudgetOverride,
  saveBudgetPolicy,
} from "../api/admin";
import { useConfirm } from "../composables/useConfirm";
import { useToast } from "../composables/useToast";
import SidePanel from "./SidePanel.vue";
import SkeletonRows from "./SkeletonRows.vue";
import StatusChip from "./StatusChip.vue";

const props = defineProps<{ devUser: string }>();
const toast = useToast();
const { confirm } = useConfirm();

const loading = ref(false);
const savingPolicy = ref(false);
const savingOverride = ref(false);
const policy = ref<BudgetPolicy | null>(null);
/** The policy form is closed until someone asks for it — see the template. */
const policyOpen = ref(false);

/**
 * A platform-default limit as prose for the summary line.
 *
 * Deliberately not `fmtLimit`, which renders null as "inherit" — correct for
 * an override row, wrong here: the platform default inherits from nothing, so
 * null means there is no ceiling at all.
 */
function fmtPolicyLimit(v: number | null | undefined): string {
  return v == null ? "no limit" : fmtMoney(v);
}
const overrides = ref<BudgetOverride[]>([]);

type Draft = {
  subject: string;
  daily_limit: string;
  monthly_limit: string;
  /** "" = inherit, "on" = block, "off" = meter only. */
  enforced: "" | "on" | "off";
  note: string;
  isNew: boolean;
};
const editing = ref<Draft | null>(null);

const editorOpen = computed({
  get: () => editing.value !== null,
  set: (v: boolean) => {
    if (!v) editing.value = null;
  },
});

function _toastError(e: unknown, context: string): void {
  toast.error(context, e instanceof AdminError ? e.message : String(e));
}

async function reload(): Promise<void> {
  loading.value = true;
  try {
    const r = await getBudgets(props.devUser);
    policy.value = r.policy;
    overrides.value = r.overrides;
  } catch (e) {
    _toastError(e, "Could not load budgets");
  } finally {
    loading.value = false;
  }
}

onMounted(reload);

const currency = computed(() => policy.value?.currency ?? "DKK");

/** Empty string means "no limit on this dimension" — the backend treats
 * null and 0 identically, so the form only has to produce null. */
function parseLimit(raw: string): number | null {
  const t = raw.trim();
  if (!t) return null;
  const n = Number(t);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

function fmtMoney(n: number | null | undefined): string {
  if (n === null || n === undefined) return "—";
  return `${n.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} ${currency.value}`;
}

function fmtLimit(n: number | null): string {
  return n === null ? "inherit" : fmtMoney(n);
}

/** Percent-of-limit for a progress bar, clamped so an over-budget
 * principal renders a full bar rather than overflowing the track. */
function pct(spend: number, limit: number | null): number {
  if (!limit) return 0;
  return Math.min(100, (spend / limit) * 100);
}

function barClass(spend: number, limit: number | null): string {
  if (!limit) return "bar__fill";
  const f = spend / limit;
  if (f >= 1) return "bar__fill bar__fill--over";
  if (f >= (policy.value?.warn_at ?? 0.8)) return "bar__fill bar__fill--warn";
  return "bar__fill";
}

async function savePolicy(): Promise<void> {
  if (!policy.value) return;
  if (!(policy.value.usd_rate > 0)) {
    toast.error("Invalid rate", "USD rate must be greater than zero.");
    return;
  }
  if (!(policy.value.warn_at > 0 && policy.value.warn_at <= 1)) {
    toast.error("Invalid threshold", "Warn threshold must be between 0 and 1.");
    return;
  }
  savingPolicy.value = true;
  try {
    // `v-model.number` on a cleared number input yields "" (or NaN), which
    // the backend's `float | None` rejects. Clearing the field means "no
    // ceiling on this dimension", so normalise it to null here.
    const body = {
      ...policy.value,
      daily_limit: parseLimit(String(policy.value.daily_limit ?? "")),
      monthly_limit: parseLimit(String(policy.value.monthly_limit ?? "")),
    };
    policy.value = await saveBudgetPolicy(body, props.devUser);
    await reload();
    toast.success("Budget policy saved");
  } catch (e) {
    _toastError(e, "Could not save budget policy");
  } finally {
    savingPolicy.value = false;
  }
}

function startNew(): void {
  editing.value = {
    subject: "",
    daily_limit: "",
    monthly_limit: "",
    enforced: "",
    note: "",
    isNew: true,
  };
}

function startEdit(row: BudgetOverride): void {
  editing.value = {
    subject: row.subject,
    daily_limit: row.daily_limit === null ? "" : String(row.daily_limit),
    monthly_limit: row.monthly_limit === null ? "" : String(row.monthly_limit),
    enforced: row.enforced === null ? "" : row.enforced ? "on" : "off",
    note: row.note,
    isNew: false,
  };
}

async function saveOverride(): Promise<void> {
  const d = editing.value;
  if (!d) return;
  const subject = d.subject.trim();
  if (!subject) {
    toast.error("Missing principal", "Enter an Entra oid or apikey:<id>.");
    return;
  }
  savingOverride.value = true;
  try {
    await saveBudgetOverride(
      subject,
      {
        daily_limit: parseLimit(d.daily_limit),
        monthly_limit: parseLimit(d.monthly_limit),
        enforced: d.enforced === "" ? null : d.enforced === "on",
        note: d.note,
      },
      props.devUser,
    );
    editing.value = null;
    await reload();
    toast.success(`Budget saved for ${subject}`);
  } catch (e) {
    _toastError(e, "Could not save budget");
  } finally {
    savingOverride.value = false;
  }
}

async function remove(row: BudgetOverride): Promise<void> {
  const ok = await confirm({
    title: "Remove budget override?",
    message: `${row.email || row.subject} falls back to the platform default (${fmtLimit(policy.value?.daily_limit ?? null)}/day, ${fmtLimit(policy.value?.monthly_limit ?? null)}/month).`,
    confirmLabel: "Remove",
  });
  if (!ok) return;
  try {
    await deleteBudgetOverride(row.subject, props.devUser);
    await reload();
    toast.success("Override removed");
  } catch (e) {
    _toastError(e, "Could not remove override");
  }
}
</script>

<template>
  <div class="admin-budgets">
    <header class="admin-budgets__header">
      <h2>Spend budgets</h2>
      <button @click="reload">Refresh</button>
    </header>

    <SkeletonRows v-if="loading" :rows="6" />

    <template v-else-if="policy">
      <!-- ── Policy ────────────────────────────────────────────────── -->
      <!-- Collapsed at rest. Eight fields nobody edits in a normal week were
           the first thing on the page, above the table people actually come
           here to read. The summary line keeps the answer visible — what the
           limits ARE — and puts the controls one click away. -->
      <section class="card">
        <div class="card__head">
          <h3>Platform default</h3>
          <button class="link" @click="policyOpen = !policyOpen">
            {{ policyOpen ? "hide" : "change" }}
          </button>
        </div>
        <p class="policy-summary">
          <StatusChip
            :tone="policy.enabled ? (policy.enforced ? 'ok' : 'warn') : 'muted'"
          >
            {{
              policy.enabled
                ? policy.enforced
                  ? "Enforced"
                  : "Measured only"
                : "Off"
            }}
          </StatusChip>
          <span>
            {{ fmtPolicyLimit(policy.daily_limit) }} a day ·
            {{ fmtPolicyLimit(policy.monthly_limit) }} a month · resets midnight
            {{ policy.timezone }}
          </span>
        </p>
        <p class="hint" v-if="policyOpen">
          Applies to every principal without an override below. Limits are in
          {{ policy.currency }}; the rate card prices in USD and the rate here
          converts. Periods reset at midnight in
          <strong>{{ policy.timezone }}</strong
          >.
        </p>
        <div class="grid" v-if="policyOpen">
          <label class="field field--check">
            <input type="checkbox" v-model="policy.enabled" />
            <span>
              Budgeting on
              <small
                >Off = no metering, no blocking, and the user meter
                hides.</small
              >
            </span>
          </label>
          <label class="field field--check">
            <input type="checkbox" v-model="policy.enforced" />
            <span>
              Block at the limit
              <small>Off = meter and warn only; nothing is ever refused.</small>
            </span>
          </label>
          <label class="field">
            Daily limit ({{ policy.currency }})
            <input
              type="number"
              min="0"
              step="1"
              v-model.number="policy.daily_limit"
            />
            <small>Empty or 0 = no daily ceiling.</small>
          </label>
          <label class="field">
            Monthly limit ({{ policy.currency }})
            <input
              type="number"
              min="0"
              step="1"
              v-model.number="policy.monthly_limit"
            />
            <small>Empty or 0 = no monthly ceiling.</small>
          </label>
          <label class="field">
            Currency
            <input type="text" maxlength="8" v-model="policy.currency" />
          </label>
          <label class="field">
            1 USD =
            <input
              type="number"
              min="0"
              step="0.01"
              v-model.number="policy.usd_rate"
            />
            <small
              >{{ policy.currency }} per USD. Re-prices spend, not
              limits.</small
            >
          </label>
          <label class="field">
            Timezone
            <input type="text" v-model="policy.timezone" />
            <small>IANA name, e.g. Europe/Copenhagen.</small>
          </label>
          <label class="field">
            Warn at
            <input
              type="number"
              min="0.1"
              max="1"
              step="0.05"
              v-model.number="policy.warn_at"
            />
            <small>
              Fraction of a limit that turns the user's meter amber ({{
                Math.round((policy.warn_at ?? 0.8) * 100)
              }}%).
            </small>
          </label>
        </div>
        <div class="actions" v-if="policyOpen">
          <button v-write class="primary" :disabled="savingPolicy" @click="savePolicy">
            {{ savingPolicy ? "Saving…" : "Save policy" }}
          </button>
        </div>
      </section>

      <!-- ── Overrides ─────────────────────────────────────────────── -->
      <section class="card">
        <div class="card__head">
          <h3>Per-principal overrides</h3>
          <button v-write @click="startNew">Add override</button>
        </div>
        <p class="hint">
          Add someone by email, or by the principal id the Usage page shows.
          Blank limits inherit the default, so you can raise one dimension
          without pinning the rest. Spend shown is live for the current period.
        </p>
        <table class="admin-table">
          <thead>
            <tr>
              <th>Principal</th>
              <th class="num">Today</th>
              <th class="num">This month</th>
              <th class="num">Daily limit</th>
              <th class="num">Monthly limit</th>
              <th>Blocking</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in overrides" :key="row.subject">
              <td>
                <div class="who" :title="row.subject">
                  {{ row.email || row.display_name || row.subject }}
                </div>
                <div v-if="row.note" class="note">{{ row.note }}</div>
              </td>
              <td class="num">
                <template v-if="row.status">
                  {{ fmtMoney(row.status.day_spend) }}
                  <div class="bar" v-if="row.status.day_limit">
                    <div
                      :class="
                        barClass(row.status.day_spend, row.status.day_limit)
                      "
                      :style="{
                        width: `${pct(row.status.day_spend, row.status.day_limit)}%`,
                      }"
                    ></div>
                  </div>
                </template>
                <span v-else class="muted">—</span>
              </td>
              <td class="num">
                <template v-if="row.status">
                  {{ fmtMoney(row.status.month_spend) }}
                  <div class="bar" v-if="row.status.month_limit">
                    <div
                      :class="
                        barClass(row.status.month_spend, row.status.month_limit)
                      "
                      :style="{
                        width: `${pct(row.status.month_spend, row.status.month_limit)}%`,
                      }"
                    ></div>
                  </div>
                </template>
                <span v-else class="muted">—</span>
              </td>
              <td class="num">{{ fmtLimit(row.daily_limit) }}</td>
              <td class="num">{{ fmtLimit(row.monthly_limit) }}</td>
              <td>
                <span v-if="row.enforced === null" class="muted">inherit</span>
                <span v-else-if="row.enforced">block</span>
                <span v-else class="muted">meter only</span>
              </td>
              <td class="row-actions">
                <button @click="startEdit(row)">Edit</button>
                <button v-write class="danger" @click="remove(row)">Remove</button>
              </td>
            </tr>
            <tr v-if="overrides.length === 0">
              <td colspan="7" class="muted">
                No overrides — everyone is on the platform default.
              </td>
            </tr>
          </tbody>
        </table>
      </section>
    </template>

    <SidePanel
      v-model="editorOpen"
      :title="editing?.isNew ? 'New budget override' : 'Edit budget override'"
    >
      <div v-if="editing" class="editor">
        <label class="field">
          Principal
          <input
            type="text"
            v-model="editing.subject"
            :disabled="!editing.isNew"
            placeholder="name@company.com, or apikey:12"
          />
          <small>
            An email (resolved to that person’s id), the principal id the Usage
            page shows under Group by → User, or
            <code>apikey:&lt;id&gt;</code>. An id is not checked against the
            user list, so you can set a budget before someone’s first turn; an
            email that matches nobody is rejected as a typo.
          </small>
        </label>
        <label class="field">
          Daily limit ({{ currency }})
          <input type="number" min="0" step="1" v-model="editing.daily_limit" />
          <small>Blank = inherit the platform default.</small>
        </label>
        <label class="field">
          Monthly limit ({{ currency }})
          <input
            type="number"
            min="0"
            step="1"
            v-model="editing.monthly_limit"
          />
          <small>Blank = inherit the platform default.</small>
        </label>
        <label class="field">
          Blocking
          <select v-model="editing.enforced">
            <option value="">Inherit the platform default</option>
            <option value="on">Block at the limit</option>
            <option value="off">Meter only — never block</option>
          </select>
          <small>
            "Meter only" still counts spend and warns; it just never refuses a
            turn. Use it for an account you must not lock out.
          </small>
        </label>
        <label class="field">
          Note
          <textarea
            rows="3"
            v-model="editing.note"
            placeholder="Why this principal has its own budget."
          ></textarea>
        </label>
        <div class="actions">
          <button
            class="primary"
            :disabled="savingOverride"
            v-write
            @click="saveOverride"
          >
            {{ savingOverride ? "Saving…" : "Save" }}
          </button>
          <button @click="editing = null">Cancel</button>
        </div>
      </div>
    </SidePanel>
  </div>
</template>

<style scoped>
.admin-budgets {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}
.admin-budgets__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.card {
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  padding: var(--space-3) var(--space-4);
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}
.policy-summary {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
  margin: 0;
  font-size: 0.9rem;
  color: var(--color-muted);
}
button.link {
  background: none;
  border: none;
  color: var(--color-accent);
  cursor: pointer;
  font-size: 0.85rem;
  padding: 0;
}
.card__head {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.card h3 {
  margin: 0;
  font-size: 1rem;
}
.hint {
  margin: 0;
  font-size: 0.85rem;
  color: var(--color-muted);
}
.grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: var(--space-3);
}
.field {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  font-size: 0.85rem;
  color: var(--color-muted);
}
.field small {
  font-size: 0.75rem;
  color: var(--color-muted);
}
.field input[type="text"],
.field input[type="number"],
.field select,
.field textarea {
  font-size: 0.92rem;
  padding: 0.35rem 0.5rem;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  background: var(--color-surface);
  color: var(--color-ink);
}
.field--check {
  flex-direction: row;
  align-items: flex-start;
  gap: 0.5rem;
}
.field--check span {
  display: flex;
  flex-direction: column;
  color: var(--color-ink);
}
.actions {
  display: flex;
  gap: 0.5rem;
  margin-top: var(--space-2);
}
.editor {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  padding: var(--space-3);
}
.admin-table .num {
  text-align: right;
  font-variant-numeric: tabular-nums;
}
.who {
  overflow: hidden;
  text-overflow: ellipsis;
}
.note {
  font-size: 0.78rem;
  color: var(--color-muted);
}
.row-actions {
  display: flex;
  gap: 0.35rem;
  justify-content: flex-end;
}
.muted {
  color: var(--color-muted);
}
.bar {
  height: 5px;
  border-radius: 3px;
  background: var(--color-surface-alt);
  overflow: hidden;
  margin-top: 3px;
}
.bar__fill {
  height: 100%;
  background: var(--color-accent);
}
.bar__fill--warn {
  background: var(--color-warn);
}
.bar__fill--over {
  background: var(--color-error);
}
</style>
